import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import SpecialSideLoginBanner from '../../../components/SpecialSideLoginBanner';
import SideBarStickyContent from '../../../components/SideBarStickyContent';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import { Tag } from '../../tagsearch/[tagIds]';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import {
  findTagsByNames,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';
import { tagsString } from '../../../common/libs/tag';
import useSpecialPage from '../../../hooks/useSpecialPage';

export const setting = {
  specialPageSlug: 'ibd-uc-cd',
  specialPageShortName: 'ibduccd',
  tagTexts: SPECIALS.ibdUcCd.tags,
  specialPagePath: SPECIALS.ibdUcCd.link,
  specialPageTitle: SPECIALS.ibdUcCd.alt,
  sideBanner: (isAuthenticated: boolean) => (
    <>
      {!isAuthenticated && (
        <SideBarStickyContent stickyHeight={45}>
          <SpecialSideLoginBanner redirectUrl={SPECIALS.ibdUcCd.link} />
        </SideBarStickyContent>
      )}
    </>
  ),
  listTitle: '#IBD #潰瘍性大腸炎 #クローン病 に関するスライド', // 一覧タイトルをタグあ配列から生成せず固定文字列にする場合はここに設定
};

const mainImagePath = SPECIALS.ibdUcCd.mainImage ?? '';
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内

const description = (
  <>
    <Text fontSize="sm" fontWeight="bold" mb={2}>
      企画概要
    </Text>
    <Text fontSize="sm">
      4月新年度、季節の変わり目のこの時期に――
      <br />
      患者さんの生活に密接に関わるIBD（炎症性腸疾患）診療は、季節の変わり目やストレス変化などが病状にも影響しやすく、丁寧なフォローアップが求められます。
      <br />
      Antaaでは、IBD診療の“リアル”を共有し合えるスライド投稿を募集しています。
      <br />
      <br />
      たとえば：
      <br />
      ・プレドニゾロン離脱のタイミングで迷った症例の対応
      <br />
      ・生物学的製剤の導入をためらった場面とその判断軸
      <br />
      ・フレアと感染症の鑑別で迷ったときのヒント
      <br />
      ・若年患者への心理的サポートやライフイベント対応の工夫
      <br />
      ・血液データや画像所見に現れにくい“違和感”の捉え方
      <br />
      <br />
      季節の変わり目に向けて、あなたの診療現場での気づきや工夫の共有をお待ちしています。
      <br />
      一枚のスライドが、誰かの診療のヒントになります。
    </Text>
    <Flex justifyContent="flex-end">
      <NextLink href={`/article/upload?tags=${setting.tagTexts[0]}`} passHref>
        <Button
          size="sm"
          as="a"
          color="white"
          bg="brand.500"
          onClick={() => {
            sendAnalyticsEvent(
              'special_contents',
              'click',
              `${setting.specialPageShortName}Upload`,
            );
          }}
          textAlign="center"
          display="inline-block"
          p={2}
          pl={{ base: 2, md: 4 }}
          pr={{ base: 2, md: 4 }}
          ml={4}
          height="auto"
          w={{ base: '50%', md: '30%' }}
          _hover={{
            opacity: '0.8',
          }}
        >
          <Text as="span">#{setting.tagTexts[0]}</Text>
          <br />
          <Text as="span" fontSize="xs">
            でスライド投稿
          </Text>
        </Button>
      </NextLink>
    </Flex>
  </>
);

// ここより下は特集ページごとの固有の内容は無し

type PageContext = {
  tags: Tag[];
  articles: ArticleHeader[];
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
};

const IbdUcCdPage: NextPage<PageContext> = ({
  tags,
  articles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
}) => {
  const {
    specialPageTitle,
    specialPagePath,
    specialPageShortName,
    sideBanner,
    listTitle,
  } = setting;

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt={specialPageTitle}
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>{description}</Box>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {articles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {listTitle || `${tagsString(tags)} に関するスライド`}
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={articles}
                      isMobile={isMobile}
                      gaEventLabel={`${specialPageShortName}_tagsearch`}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {sideBanner(isAuthenticated)}
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default IbdUcCdPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { specialPageSlug, tagTexts } = setting;

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let articles: ArticleHeader[] = [];
    const targetTags = await findTagsByNames(tagTexts);
    const tags: Tag[] = targetTags.map((tag) => ({
      id: tag.tag_id,
      name: tag.tag_name,
    }));
    if (tags.length) {
      const articleIdList: string[] = await getArticleIdsWithTagIds(
        tags.map((tag) => tag.id),
      );

      const articleHeaders = await prisma.article_header.findMany({
        select: {
          article_id: true,
          name: true,
          article_type: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIdList,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
        orderBy: {
          create_date: 'desc',
        },
      });
      // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
      const discloseIds = articleHeaders
        .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
        .filter((v) => !!v);
      // アカウント統合済みのユーザのプロフィールのみ返却される
      const idBaseProfileMap = await fetchAccountIntegrationProfiles(
        discloseIds,
      );

      articles = articleHeaders.reduce((articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: article.user_article_header_author_idTouser.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds:
              article.user_article_header_author_idTouser.update_date.getTime(),
            profilePhotoS3Key:
              article.user_article_header_author_idTouser.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      }, []);
    }

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;

    return {
      props: {
        tags,
        articles,
        isMobile,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isDemoAccount: hasDemoAuthority(context.req.user?.userRoles),
        isPrivate,
      },
    };
  },
);
